import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactModal from 'react-modal';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import { debounce } from 'lodash';

ReactModal.setAppElement('#root'); // Asegura que el modal esté asociado a tu root

const ListadoClientes = () => {
    const [allClientes, setAllClientes] = useState([]); // Lista completa para referencia
    const [listados, setListados] = useState([]);
    const [filteredListados, setFilteredListados] = useState([]);
    const [file, setFile] = useState(null);
    const [mensaje, setMensaje] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkIva, setBulkIva] = useState(false);
    const [bulkRenta, setBulkRenta] = useState(false);
    const [bulkArriendo, setBulkArriendo] = useState(false);
    const [editingCliente, setEditingCliente] = useState(null);
    const [formData, setFormData] = useState({ nombre: '', rut: '', clave: '', banco: '', cuenta: '', iva: false, renta: false, arriendo: false });
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newCliente, setNewCliente] = useState({
        nombre: '',
        rut: '',
        clave: '',
        banco: '',
        cuenta: '',
        iva: false,
        renta: false,
        arriendo: false
    });

    // Estados para paginación y búsqueda
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    const [limit] = useState(50); // Elementos por página


     // Cargar el listado de clientes con paginación
    const fetchListados = async (pageNumber = 1, searchTerm = '') => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/listados`, {
                params: {
                    page: pageNumber,
                    limit,
                    search: searchTerm
                }
            });
            
            // Si el backend devuelve un objeto con datos paginados
            if (response.data.clientes) {
                setListados(response.data.clientes);
                setFilteredListados(response.data.clientes);
                setTotalPages(response.data.totalPages);
            } else {
                // Si el backend devuelve un array simple (para compatibilidad)
                setListados(response.data);
                setFilteredListados(response.data);
                // Calcular páginas totales manualmente
                setTotalPages(Math.ceil(response.data.length / limit));
            }
            
            // Guardar todos los clientes para la búsqueda completa
            if (searchTerm === '') {
                const allResponse = await axios.get(`${process.env.REACT_APP_API_URL || ''}/listados/all`);
                setAllClientes(allResponse.data);
            }
        } catch (error) {
            console.error("Error al cargar clientes:", error);
            setMensaje("Error al cargar la lista de clientes");
            setListados([]);
            setFilteredListados([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchListados(page, search);
    }, [page]);



     // Búsqueda con debounce
     const handleSearch = useCallback(
        debounce(async (searchTerm) => {
            setSearch(searchTerm);
            setPage(1); // Reiniciar a la primera página al buscar
            
            if (!searchTerm.trim()) {
                // Si la búsqueda está vacía, simplemente cargar la página actual
                fetchListados(1, '');
                return;
            }
            
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/listados`, {
                    params: {
                        page: 1,
                        limit,
                        search: searchTerm
                    }
                });
                
                if (response.data.clientes) {
                    setFilteredListados(response.data.clientes);
                    setTotalPages(response.data.totalPages);
                } else {
                    // Buscar localmente si el backend no soporta búsqueda
                    const filtered = allClientes.filter(cliente => 
                        cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        cliente.rut.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                    setFilteredListados(filtered.slice(0, limit));
                    setTotalPages(Math.ceil(filtered.length / limit));
                }
            } catch (error) {
                console.error("Error en búsqueda:", error);
                setMensaje("Error al realizar la búsqueda");
            } finally {
                setLoading(false);
            }
        }, 300),
        [allClientes]
    );


      // Manejar cambios en el input de búsqueda
      const handleSearchInput = (e) => {
        const value = e.target.value;
        setSearch(value);
        handleSearch(value);
    };
    


    // Manejar cambio de archivo CSV para carga masiva
    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) return;
        const data = new FormData();
        data.append('file', file);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL || ''}/listados/upload`, data);
            setMensaje(`Carga masiva exitosa. ${res.data.resultados.length} registros procesados.`);
            fetchListados();
        } catch (error) {
            setMensaje(`Error en carga masiva: ${error.response?.data?.mensaje || error.message}`);
        }
    };

    // Manejo de selección masiva para IVA, renta y arriendo
    const handleToggleSelect = (id) => {
        const newSet = new Set(selectedIds);
        newSet.has(id) ? newSet.delete(id) : newSet.add(id);
        setSelectedIds(newSet);
    };

    const handleBulkUpdate = async () => {
        try {
            const updates = Array.from(selectedIds).map(id => {
                return axios.put(`${process.env.REACT_APP_API_URL || ''}/listados/${id}`, {
                    iva: bulkIva,
                    renta: bulkRenta,
                    arriendo: bulkArriendo
                });
            });
            await Promise.all(updates);
            setMensaje('Actualización masiva realizada.');
            setSelectedIds(new Set());
            fetchListados();
        } catch (error) {
            console.error(error);
            setMensaje('Error en actualización masiva.');
        }
    };

    // Función para seleccionar/deseleccionar todos
    const handleSelectAll = () => {
        if (selectedIds.size === listados.length) {
            setSelectedIds(new Set());
        } else {
            const newSelected = new Set(listados.map(cliente => cliente.id));
            setSelectedIds(newSelected);
        }
    };

    // Función para actualizar inline un campo (iva, renta, arriendo) de un cliente
    const handleInlineUpdate = async (id, field, newValue) => {
        try {
            // Actualización optimista en la UI
            setListados(prev =>
                prev.map(cliente =>
                    cliente.id === id ? { ...cliente, [field]: newValue } : cliente
                )
            );
            await axios.put(`${process.env.REACT_APP_API_URL || ''}/listados/${id}`, { [field]: newValue });
        } catch (error) {
            console.error('Error updating client inline', error);
            setMensaje('Error al actualizar el cliente.');
            // En caso de error, se puede recargar la data
            fetchListados();
        }
    };

    // Edición individual
    const handleEditClick = (cliente) => {
        setEditingCliente(cliente.id);
        setFormData({
            nombre: cliente.nombre,
            rut: cliente.rut,
            clave: cliente.clave,
            iva: cliente.iva,
            renta: cliente.renta,
            arriendo: cliente.arriendo
        });
        setIsEditModalOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleEditSave = async () => {
        try {
            await axios.put(`${process.env.REACT_APP_API_URL || ''}/listados/${editingCliente}`, formData);
            setMensaje('Cliente actualizado.');
            setIsEditModalOpen(false);
            setEditingCliente(null);
            fetchListados();
        } catch (error) {
            console.error(error);
            setMensaje('Error al actualizar cliente.');
        }
    };


    // Función para crear nuevo cliente
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_API_URL || ''}/listados`, newCliente);
            setMensaje('Cliente creado exitosamente');
            setShowCreateForm(false);
            setNewCliente({
                nombre: '',
                rut: '',
                clave: '',
                banco: '',
                cuenta: '',
                iva: false,
                renta: false,
                arriendo: false
            });
            fetchListados();
        } catch (error) {
            setMensaje('Error al crear el cliente: ' + error.response?.data?.error || error.message);
        }
    };


    const handleDelete = (id) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${process.env.REACT_APP_API_URL || ''}/listados/${id}`);
                    setListados(listados.filter((cliente) => cliente.id !== id));
                    Swal.fire('Eliminado', 'El cliente ha sido eliminado.', 'success');
                } catch (err) {
                    console.error(err);
                    Swal.fire('Error', 'No se pudo eliminar el cliente.', 'error');
                }
            }
        });
    };


    const closeModal = () => {
        setIsEditModalOpen(false);
        setEditingCliente(null);
    };

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold">Listado Clientes</h1>
                <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                    Nuevo Cliente
                </button>
            </div>

            {/* Formulario de creación */}
            {showCreateForm && (
                <div className="bg-white shadow rounded p-4 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Crear Nuevo Cliente</h2>
                    <form onSubmit={handleCreateSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Apellido Nombre o Razón Social</label>
                                <input
                                    type="text"
                                    value={newCliente.nombre}
                                    onChange={(e) => setNewCliente({ ...newCliente, nombre: e.target.value })}
                                    className="w-full border p-2 rounded"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">RUT</label>
                                <input
                                    type="text"
                                    value={newCliente.rut}
                                    onChange={(e) => setNewCliente({ ...newCliente, rut: e.target.value })}
                                    className="w-full border p-2 rounded"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Clave</label>
                                <input
                                    type="text"
                                    value={newCliente.clave}
                                    onChange={(e) => setNewCliente({ ...newCliente, clave: e.target.value })}
                                    className="w-full border p-2 rounded"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Banco</label>
                                <input
                                    type="text"
                                    value={newCliente.banco}
                                    onChange={(e) => setNewCliente({ ...newCliente, banco: e.target.value })}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Cuenta</label>
                                <input
                                    type="text"
                                    value={newCliente.cuenta}
                                    onChange={(e) => setNewCliente({ ...newCliente, cuenta: e.target.value })}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                        </div>
                        <div className="flex space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={newCliente.iva}
                                    onChange={(e) => setNewCliente({ ...newCliente, iva: e.target.checked })}
                                    className="mr-2"
                                />
                                IVA
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={newCliente.renta}
                                    onChange={(e) => setNewCliente({ ...newCliente, renta: e.target.checked })}
                                    className="mr-2"
                                />
                                Renta
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={newCliente.arriendo}
                                    onChange={(e) => setNewCliente({ ...newCliente, arriendo: e.target.checked })}
                                    className="mr-2"
                                />
                                Arriendo
                            </label>
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                onClick={() => setShowCreateForm(false)}
                                className="px-4 py-2 border rounded"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-500 text-white rounded"
                            >
                                Guardar
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Sección de carga masiva */}
            <div className="bg-white shadow rounded p-4 mb-6">
                <h2 className="text-xl font-semibold mb-2">Carga Masiva</h2>
                <div className="flex items-center space-x-4">
                    <input type="file" onChange={handleFileChange} accept=".csv" className="border p-2 rounded" />
                    <button onClick={handleUpload} className="bg-blue-500 text-white px-4 py-2 rounded">Subir CSV</button>
                </div>
                {mensaje && <p className="mt-2 text-sm text-green-600">{mensaje}</p>}
            </div>

            {/* Búsqueda */}
            <div className="bg-white shadow rounded p-4 mb-6">
                <div className="relative">
                    <input
                        type="text"
                        value={search}
                        onChange={handleSearchInput}
                        placeholder="Buscar por nombre o RUT..."
                        className="w-full p-2 pl-8 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
            </div>

           

            {/* Listado de clientes */}
            <div className="bg-white shadow rounded overflow-x-auto">
                {loading ? (
                    <div className="text-center p-6">Cargando...</div>
                ) : filteredListados.length === 0 ? (
                    <div className="text-center p-6 text-gray-500">
                        No hay clientes que coincidan con la búsqueda.
                    </div>
                ) : (
                    <table className="min-w-full table-auto">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="p-2 border">Nombre</th>
                                <th className="p-2 border">RUT</th>
                                <th className="p-2 border">Clave</th>
                                <th className="p-2 border">Banco</th>
                                <th className="p-2 border">Cuenta</th>
                                <th className="p-2 border">IVA</th>
                                <th className="p-2 border">Renta</th>
                                <th className="p-2 border">Arriendo</th>
                                <th className="p-2 border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredListados.map((cliente) => (
                                <tr key={cliente.id} className="text-center hover:bg-gray-50">
                                    <td className="p-2 border">{cliente.nombre}</td>
                                    <td className="p-2 border">{cliente.rut}</td>
                                    <td className="p-2 border">{cliente.clave}</td>
                                    <td className="p-2 border">{cliente.banco}</td>
                                    <td className="p-2 border">{cliente.cuenta}</td>
                                    <td className="p-2 border">
                                        <input
                                            type="checkbox"
                                            checked={cliente.iva}
                                            onChange={() => handleInlineUpdate(cliente.id, 'iva', !cliente.iva)}
                                            className="h-5 w-5 text-green-500"
                                        />
                                    </td>
                                    <td className="p-2 border">
                                        <input
                                            type="checkbox"
                                            checked={cliente.renta}
                                            onChange={() => handleInlineUpdate(cliente.id, 'renta', !cliente.renta)}
                                            className="h-5 w-5 text-green-500"
                                        />
                                    </td>
                                    <td className="p-2 border">
                                        <input
                                            type="checkbox"
                                            checked={cliente.arriendo}
                                            onChange={() => handleInlineUpdate(cliente.id, 'arriendo', !cliente.arriendo)}
                                            className="h-5 w-5 text-green-500"
                                        />
                                    </td>
                                    <td className="p-2 border">
                                        <div className="flex justify-center space-x-2">
                                            <button
                                                onClick={() => handleEditClick(cliente)}
                                                className="bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-yellow-600"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(cliente.id)}
                                                className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Paginación */}
            {filteredListados.length > 0 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                    <button
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        {'<<'}
                    </button>
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        {'<'}
                    </button>

                    <div className="flex gap-1">
                        {[...Array(totalPages)].map((_, i) => {
                            const pageNumber = i + 1;
                            const isCurrentPage = page === pageNumber;
                            const isClosePage = 
                                pageNumber === 1 || 
                                pageNumber === totalPages || 
                                (pageNumber >= page - 1 && pageNumber <= page + 1);
                            
                            if (!isClosePage && pageNumber !== 1 && pageNumber !== totalPages - 1 && pageNumber !== totalPages) {
                                if (pageNumber === page - 2 || pageNumber === page + 2) {
                                    return <span key={pageNumber} className="px-3 py-1">...</span>;
                                }
                                return null;
                            }
                            
                            return (
                                <button
                                    key={pageNumber}
                                    onClick={() => setPage(pageNumber)}
                                    className={`px-3 py-1 border rounded-md text-sm ${
                                        isCurrentPage
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-white hover:bg-gray-50'
                                    }`}
                                >
                                    {pageNumber}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        {'>'}
                    </button>
                    <button
                        onClick={() => setPage(totalPages)}
                        disabled={page === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        {'>>'}
                    </button>
                </div>
            )}

            {/* Modal para edición de cliente */}
            <ReactModal
                isOpen={isEditModalOpen}
                onRequestClose={closeModal}
                className="bg-white p-6 rounded shadow-lg w-full max-w-2xl mx-auto mt-20"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start"
            >
                <h2 className="text-xl font-bold mb-6">Editar Cliente</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Nombre</label>
                        <input
                            type="text"
                            name="nombre"
                            value={formData.nombre}
                            onChange={handleEditChange}
                            className="w-full border p-2 rounded"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">RUT</label>
                        <input
                            type="text"
                            name="rut"
                            value={formData.rut}
                            onChange={handleEditChange}
                            className="w-full border p-2 rounded"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Clave</label>
                        <input
                            type="text"
                            name="clave"
                            value={formData.clave}
                            onChange={handleEditChange}
                            className="w-full border p-2 rounded"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Banco</label>
                        <input
                            type="text"
                            name="banco"
                            value={formData.banco}
                            onChange={handleEditChange}
                            className="w-full border p-2 rounded"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Cuenta</label>
                        <input
                            type="text"
                            name="cuenta"
                            value={formData.cuenta}
                            onChange={handleEditChange}
                            className="w-full border p-2 rounded"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <div className="flex space-x-6">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="iva"
                                    checked={formData.iva}
                                    onChange={handleEditChange}
                                    className="h-5 w-5 text-green-500 mr-2"
                                />
                                IVA
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="renta"
                                    checked={formData.renta}
                                    onChange={handleEditChange}
                                    className="h-5 w-5 text-green-500 mr-2"
                                />
                                Renta
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="arriendo"
                                    checked={formData.arriendo}
                                    onChange={handleEditChange}
                                    className="h-5 w-5 text-green-500 mr-2"
                                />
                                Arriendo
                            </label>
                        </div>
                    </div>
                </div>
                <div className="mt-6 flex justify-end space-x-4">
                    <button
                        onClick={closeModal}
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleEditSave}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                        Guardar
                    </button>
                </div>
            </ReactModal>
        </div>
    );
};

export default ListadoClientes;